import type {
  DismissRuleSuggestion,
  DismissSecret,
  OpenRouterModel,
  ProjectDto,
  ProjectRuleDto,
  ReviewDetailDto,
  ReviewDto,
  ReviewEventDto,
  ReviewPermissions,
  RuleDto,
  SecretOutsideDiffDto,
  SetOverride,
  SettingsDto,
  SpendBucket,
  SpendSeries,
  StatsOverview,
  SyncStatus,
  UpdateProject,
  UpdateSettings,
} from "@reviewer73/shared";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: init.body ? { "Content-Type": "application/json" } : {},
    ...init,
  });
  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // keep statusText
    }
    throw new ApiError(response.status, message);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function loginUrl(returnTo: string): string {
  return `/auth/gitlab?returnTo=${encodeURIComponent(returnTo)}`;
}

export const api = {
  me: () =>
    request<{ userId: number; username: string; dev?: boolean; gitlabUrl: string }>("/api/me"),
  logout: () => request<{ ok: true }>("/api/logout", { method: "POST" }),

  projects: () => request<ProjectDto[]>("/api/projects"),
  updateProject: (id: string, body: UpdateProject) =>
    request<{ ok: true }>(`/api/projects/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  projectRules: (id: string) => request<ProjectRuleDto[]>(`/api/projects/${id}/rules`),
  setOverride: (id: string, ruleId: string, body: SetOverride) =>
    request<{ ok: true }>(`/api/projects/${id}/rules/${ruleId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  clearOverride: (id: string, ruleId: string) =>
    request<{ ok: true }>(`/api/projects/${id}/rules/${ruleId}`, { method: "DELETE" }),
  projectReviews: (id: string) =>
    request<{ reviews: ReviewDto[]; total: number }>(`/api/projects/${id}/reviews`),

  models: () => request<OpenRouterModel[]>("/api/models"),

  settings: () => request<SettingsDto>("/api/settings"),
  updateSettings: (body: UpdateSettings) =>
    request<{ ok: true }>("/api/settings", { method: "PATCH", body: JSON.stringify(body) }),

  rules: () => request<{ rules: RuleDto[]; syncStatus: SyncStatus }>("/api/rules"),
  syncRules: () => request<SyncStatus>("/api/rules/sync", { method: "POST" }),

  reviews: (params: { projectId?: string; state?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.projectId) query.set("projectId", params.projectId);
    if (params.state) query.set("state", params.state);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return request<ReviewDto[]>(`/api/reviews${suffix}`);
  },
  review: (id: string) => request<ReviewDetailDto>(`/api/reviews/${id}`),
  reviewEvents: (id: string, opts: { afterSeq?: number; kinds?: string[] } = {}) => {
    const query = new URLSearchParams();
    if (opts.afterSeq !== undefined) query.set("afterSeq", String(opts.afterSeq));
    if (opts.kinds?.length) query.set("kinds", opts.kinds.join(","));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return request<ReviewEventDto[]>(`/api/reviews/${id}/events${suffix}`);
  },
  /** `scope: "architecture"` runs only the architecture step — no line review, no scan. */
  createReview: (body: { projectId: string; mrIid: number; scope?: "full" | "architecture" }) =>
    request<{ reviewId: string }>("/api/reviews", { method: "POST", body: JSON.stringify(body) }),

  cancelReview: (id: string) => request<{ ok: true }>(`/api/reviews/${id}/cancel`, { method: "POST" }),
  reviewPermissions: (id: string) => request<ReviewPermissions>(`/api/reviews/${id}/permissions`),

  stats: (days: number) => request<StatsOverview>(`/api/stats/overview?days=${days}`),
  /** Cost over time. Its own call, so changing bucket or repo leaves the rest of the page alone. */
  spend: (params: { days: number; bucket: SpendBucket; projectId?: string }) => {
    const query = new URLSearchParams({ days: String(params.days), bucket: params.bucket });
    if (params.projectId) query.set("projectId", params.projectId);
    return request<SpendSeries>(`/api/stats/spend?${query.toString()}`);
  },
  observations: () =>
    request<
      Array<{
        suggestedRule: string | null;
        message: string;
        file: string;
        line: number;
        projectPath: string;
        mrIid: number;
      }>
    >("/api/observations"),
  /** A verdict on a whole suggestion; the suggestion text is the key, "" the unnamed group. */
  dismissRuleSuggestion: (body: DismissRuleSuggestion) =>
    request<{ ok: true }>("/api/rule-suggestions/dismiss", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  restoreRuleSuggestion: (suggestion: string) =>
    request<{ ok: true }>(
      `/api/rule-suggestions/dismiss?suggestion=${encodeURIComponent(suggestion)}`,
      { method: "DELETE" },
    ),
  /** Omitting `status` yields the open funds — what the nav badge counts. */
  secretsOutsideDiff: (params: { status?: string; projectId?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.projectId) query.set("projectId", params.projectId);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return request<SecretOutsideDiffDto[]>(`/api/secrets-outside-diff${suffix}`);
  },
  dismissSecret: (id: string, body: DismissSecret) =>
    request<{ ok: true }>(`/api/secrets-outside-diff/${id}/dismiss`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  restoreSecret: (id: string) =>
    request<{ ok: true }>(`/api/secrets-outside-diff/${id}/dismiss`, { method: "DELETE" }),
};
